import { Badge, Button, Flex, Table, Text, Dialog, Section, TextField } from "@radix-ui/themes";
import { useState } from "react";
import { toast } from "sonner";
import { useFeatureFlag } from "configcat-react";

import { DropdownMenu } from "@/components/shared/dropdown-menu";
import {
  DotsVerticalIcon,
  Pencil1Icon,
  TrashIcon,
  Link2Icon,
  PlusIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import Switch from "@/components/shared/inputs/switch";
import { AIsheet } from "@/v2/assets/icons/AIsheet";
import { useEnrichmentApi } from "@/v2/entities/enrichment/Enrichment.api";
import { useMutation, useQuery } from "react-query";
import { Enrichment } from "@/v2/entities/enrichment/Enrichment.model";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";

export function Enrichments() {
  const enrichmentApi = useEnrichmentApi();
  const skillConnectionEnabled = useFlag("skill-connection");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["enrichments"],
    queryFn: () => enrichmentApi.getAll(),
  });
  const [searchPhrase, setSearchPhrase] = useState("");

  const navigate = useNavigate();

  return (
    <PageLayoutV2>
      <Flex direction="column">
          <PageHeader
            title="Enrichments"
            description="Enhance your AI agents with custom enrichment tasks"
            icon={<AIsheet size={24} fill={PageColors[ENavigate.ENRICHMENTS] || "gray"} />}
            actions={
              <Flex gap="2" align="center">
                <TextField.Root
                  placeholder="Search enrichments"
                  size="2"
                  value={searchPhrase}
                  onChange={(e) => setSearchPhrase(e.target.value)}
                >
                  <TextField.Slot>
                    <MagnifyingGlassIcon height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
                <Button
                  highContrast
                  color="gray"
                  onClick={() => navigate("/enrichments/create")}
                >
                  <PlusIcon />
                  Create Enrichment
                </Button>
              </Flex>
            }
          />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="3" width="100%">
            <Flex direction={"row"} gap={"2"} align="center">
              <Flex gap={"2"} align={"center"}>
                <Text size={"3"} weight={"medium"}>
                  Enrichments
                </Text>
                <Badge color="gray">{data?.length}</Badge>
              </Flex>
            </Flex>
            <Table.Root>
          <Table.Header>
            <Table.Row className=" text-gray-500 font-normal">
              <Table.ColumnHeaderCell className=" font-normal w-2/6">
                Enrichment
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/2">
                Task
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/12">
                Trigger
              </Table.ColumnHeaderCell>
              {skillConnectionEnabled && (
                <Table.ColumnHeaderCell className=" font-normal w-1/12">
                  Skill Connection
                </Table.ColumnHeaderCell>
              )}
              <Table.ColumnHeaderCell className=" font-normal w-1/12">
                Status
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/12">
                Created At
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/12">
                Actions
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/12"></Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data?.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={skillConnectionEnabled ? 8 : 7}>
                  <Flex
                    align="center"
                    justify="center"
                    py="6"
                    direction="column"
                    gap="2"
                  >
                    <Text size="2" color="gray">
                      No enrichments found
                    </Text>
                    <Text size="2" color="gray">
                      Create a new enrichment to get started
                    </Text>
                    <Button
                      color="gray"
                      highContrast
                      onClick={() => navigate("/enrichments/create")}
                      className="mt-2"
                    >
                      Create Enrichment
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )}
            {data
              ?.filter(
                (enrichment) =>
                  enrichment.name
                    .toLowerCase()
                    .includes(searchPhrase.toLowerCase()) ||
                  enrichment.task
                    .toLowerCase()
                    .includes(searchPhrase.toLowerCase())
              )
              .map((enrichment) => (
                <EnrichmentItem
                  key={enrichment.id}
                  enrichment={enrichment}
                  refetch={refetch}
                  skillConnectionEnabled={skillConnectionEnabled}
                />
              ))}
          </Table.Body>
        </Table.Root>
      </Flex>
    </Section>
  </Flex>
</PageLayoutV2>
  );
}

interface EnrichmentItemProps {
  enrichment: Enrichment;
  refetch: () => void;
  skillConnectionEnabled: boolean;
}

function EnrichmentItem({
  enrichment,
  refetch,
  skillConnectionEnabled,
}: EnrichmentItemProps) {
  const enrichmentApi = useEnrichmentApi();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [enrichmentName, setEnrichmentName] = useState(enrichment.name);
  const [task, setTask] = useState(enrichment.task);
  const [isEnabled, setIsEnabled] = useState(enrichment.isEnabled);
  const [filterRules, setFilterRules] = useState(enrichment.filterRules);
  const [options, setOptions] = useState(enrichment.options);
  const [createdBy, setCreatedBy] = useState(enrichment.createdBy || "");
  const navigate = useNavigate();

  const deleteEnrichmentMutation = useMutation({
    mutationFn: enrichmentApi.delete,
  });
  const updateEnrichmentMutation = useMutation({
    mutationFn: enrichmentApi.update,
  });

  const handleToggleStatus = async (newValue: boolean) => {
    try {
      await updateEnrichmentMutation.mutateAsync({
        id: enrichment.id,
        name: enrichmentName,
        task: task,
        filterRules: filterRules,
        options: options,
        isEnabled: newValue,
        trigger: enrichment.trigger,
        output: enrichment.output,
        sources: enrichment.sources,
      });
      setIsEnabled(!isEnabled);
      toast(`Enrichment ${!isEnabled ? "enabled" : "disabled"} successfully`);
      refetch();
    } catch (error) {
      toast.error("Failed to toggle enrichment status");
    }
  };

  function handleRowClick() {
    navigate(`/enrichments/${enrichment.id}/edit`);
  }

  function getTriggerBadgeColor() {
    switch (enrichment.trigger) {
      case "ON_FINITE_STATE":
        return "blue";
      case "ON_CREATION":
        return "green";
      case "ON_UPDATE":
        return "orange";
      default:
        return "gray";
    }
  }

  function formatTriggerText(trigger: string) {
    return trigger.replace(/_/g, " ").toLowerCase();
  }

  return (
    <>
      <Table.Row
        className="cursor-pointer hover:bg-gray-100"
        onClick={handleRowClick}
      >
        <Table.Cell>
          <Flex align={"center"} gap={"2"}>
            <Flex
              align="center"
              justify="center"
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: "var(--plum-3)",
                borderRadius: "4px",
              }}
            >
              <AIsheet size={16} fill="plum" />
            </Flex>

            <Text size={"2"} color="gray" weight="medium" highContrast>
              {enrichment.name}
            </Text>
            {enrichment.copilotBlocks &&
              enrichment.copilotBlocks?.length > 0 && (
                <Badge color="blue">Copilot block</Badge>
              )}
          </Flex>
        </Table.Cell>
        <Table.Cell>{enrichment.task}</Table.Cell>
        <Table.Cell>
          <Badge color={getTriggerBadgeColor()} className="capitalize">
            {formatTriggerText(enrichment.trigger)}
          </Badge>
        </Table.Cell>
        {skillConnectionEnabled && (
          <Table.Cell>
            {enrichment.skillConnection ? (
              <Badge
                color={enrichment.skillConnection.isActive ? "green" : "gray"}
              >
                {enrichment.skillConnection.skill.name}
              </Badge>
            ) : (
              <Text size="2" color="gray">
                None
              </Text>
            )}
          </Table.Cell>
        )}
        <Table.Cell onClick={(e) => e.stopPropagation()}>
          <Flex align="center" gap="2">
            <div className="flex items-center gap-2">
              <Switch value={isEnabled} onChange={handleToggleStatus} />
            </div>
          </Flex>
        </Table.Cell>

        <Table.Cell>
          {new Date(enrichment.createdAt).toLocaleDateString()}
        </Table.Cell>
        <Table.Cell onClick={(e) => e.stopPropagation()}>
          <DropdownMenu
            items={[
              {
                label: "Edit",
                icon: <Pencil1Icon />,
                onClick: () => navigate(`/enrichments/${enrichment.id}/edit`),
              },
              ...(skillConnectionEnabled
                ? [
                    {
                      label: "Connect to Skill",
                      icon: <Link2Icon />,
                      onClick: () =>
                        navigate(
                          `/enrichments/${enrichment.id}/skill-connection`
                        ),
                    },
                  ]
                : []),
              {
                label: "Delete",
                icon: <TrashIcon />,
                onClick: async () => {
                  try {
                    await deleteEnrichmentMutation.mutateAsync(enrichment.id);
                    toast("Enrichment deleted successfully");
                    refetch();
                  } catch (error) {
                    toast.error(
                      "Failed to delete enrichment because of related tickets"
                    );
                  }
                },
              },
            ]}
          >
            <DropdownMenu.Trigger>
              <Button
                variant="ghost"
                size="1"
                color="gray"
                highContrast
                onClick={(e) => e.stopPropagation()}
              >
                <DotsVerticalIcon />
              </Button>
            </DropdownMenu.Trigger>
          </DropdownMenu>
        </Table.Cell>
      </Table.Row>
    </>
  );
}
